import { useId, useState } from 'react'
import { faPaperclip, faPaperPlane } from '@fortawesome/pro-solid-svg-icons'
import { Icon, Button, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { AttachmentDropZone } from './AttachmentDropZone'
import { UploadingHint } from './UploadingHint'
import { useAttachmentsWithAnalytics } from '../hooks/useAttachmentsWithAnalytics'
import type { AttachmentRef } from '../lib/attachments.types'

const ATTACHMENT_SENT_MESSAGE = 'I am sending a file for this request.'

interface RequestedAttachmentProps {
  disabled?: boolean
  onSend: (
    message: string,
    options?: {
      pendingAttachments?: AttachmentRef[]
      attachmentFiles?: File[]
    }
  ) => void
}

export const RequestedAttachment = ({
  disabled = false,
  onSend,
}: RequestedAttachmentProps) => {
  const uploadingHintId = useId()
  const [sent, setSent] = useState(false)
  const {
    items,
    add,
    retry,
    cancel,
    remove,
    refs,
    uploadedFiles,
    isUploading,
    isFull,
  } = useAttachmentsWithAnalytics()

  const sendDisabled = disabled || sent || isUploading || refs.length === 0

  const handleSend = () => {
    setSent(true)
    onSend(ATTACHMENT_SENT_MESSAGE, {
      pendingAttachments: refs,
      attachmentFiles: uploadedFiles,
    })
  }

  return (
    <div
      className={mtc(
        'my-2 rounded-[24px] overflow-hidden',
        'border border-neutral-200 bg-neutral-0',
        'p-6 flex flex-col gap-4',
        'shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-1px_rgba(0,0,0,0.06)]'
      )}
    >
      <div className="flex items-center gap-4">
        <div className="flex items-center justify-center size-8 rounded-[8px] bg-moss-25 shrink-0">
          <Icon icon={faPaperclip} className="text-[16px] text-moss-200" />
        </div>
        <Text variant={TypographyStyles.LABEL_MD} color={TextColors.HEADER}>
          Attach a file
        </Text>
      </div>

      <AttachmentDropZone
        items={items}
        onAdd={add}
        onRetry={retry}
        onCancel={cancel}
        onRemove={remove}
        disabled={disabled || sent}
        isFull={isFull}
      />

      <div className="flex flex-col items-end gap-2">
        {isUploading && <UploadingHint id={uploadingHintId} />}
        <Button
          variant="primary"
          size="sm"
          iconLeft={faPaperPlane}
          onClick={handleSend}
          disabled={sendDisabled}
          aria-describedby={isUploading ? uploadingHintId : undefined}
        >
          Send attachment
        </Button>
      </div>
    </div>
  )
}
